import { enumMembers, type ApiSchema } from "./apiSchema";
import { readArray, readBoolean, readInteger, readNullableString, readObject, readOneOf, readString, type JsonObject } from "./http/jsonDecoding";

/**
 * A connector's presence and its latest capability report (`ConnectorStatusBody`;
 * connector-protocol.md "Pairing", connector.md CN-4): what the host can choose when a
 * Thread goes live on it. Nothing here is secret; API keys are reported as present or not.
 */

export type AgentStatus = ApiSchema<"AgentStatus">;
export type ApiRouteStatus = ApiSchema<"ApiRouteStatus">;
export type ApiKind = ApiSchema<"ApiKind">;
export type ProviderRouteState = ApiSchema<"ProviderRouteState">;
export type FactSource = ApiSchema<"FactSource">;

const AGENT_STATUSES = enumMembers<AgentStatus>({ ready: true, not_installed: true, not_logged_in: true, failed: true });
const API_ROUTE_STATUSES = enumMembers<ApiRouteStatus>({ ready: true, failed: true });
const API_KINDS = enumMembers<ApiKind>({ openai: true, anthropic: true, gemini: true, openai_compatible: true });
const PROVIDER_ROUTE_STATES = enumMembers<ProviderRouteState>({
  ok: true,
  invalid_key: true,
  insufficient_credits: true,
  rate_limited: true,
  unavailable: true,
});
export const FACT_SOURCES = enumMembers<FactSource>({ reported: true, probed: true, observed: true, override_host: true });
export type ItemToolsWorkspace = ApiSchema<"ItemToolsWorkspace">;
const ITEM_TOOLS_WORKSPACE = enumMembers<ItemToolsWorkspace>({ none: true, read: true, read_write: true });

/** A permission profile on the host's PC (connector.md CN-8). */
export interface ReportedProfile {
  readonly name: string;
  /** Whether reviewed tools may use its workspace (Q-81); `none` from older connectors. */
  readonly itemToolsWorkspace: ItemToolsWorkspace;
}

export interface OfferedModel {
  readonly modelId: string;
  readonly displayName: string;
}

/** A reasoning effort an agent offers for its default LLM (its ACP `thought_level` values). */
export interface OfferedEffort {
  readonly effortId: string;
  readonly displayName: string;
}

/** Why a connector can't run a route now, as the server decides it for dispatch too (CP-11). */
export type RouteUnavailability = ApiSchema<"RouteUnavailability">;

export interface ReportedAgent {
  readonly agentId: string;
  readonly status: AgentStatus;
  /** The server's answer: `null` when it can run turns now, else why not (CP-11). */
  readonly unavailableBecause: RouteUnavailability | null;
  /** What the agent calls itself, if it said. */
  readonly title: string | null;
  readonly models: readonly OfferedModel[];
  /** ISO 8601: when its latest session listed the models; `null` before any. */
  readonly modelsObservedAt: string | null;
  /** The LLM a new session starts with; `null` before any session or from older connectors. */
  readonly defaultModelId: string | null;
  /** Empty when it offers none. */
  readonly reasoningEfforts: readonly OfferedEffort[];
  /** What came of the agent's own model catalogue, in counts; `null` without one. */
  readonly catalogue: ReportedCatalogue | null;
  /** Why the agent does not work, as the connector says, such as facts that exceed the report. */
  readonly problem: string | null;
  /** Its optional facts left out at the report's shared limit: a diagnostic, never a block. */
  readonly omittedFacts: number;
}

export interface ReportedCatalogue {
  readonly state: "read" | "failed";
  readonly reported: number;
  /** Rows the protocol would refuse: left out. */
  readonly invalid: number;
  /** Valid rows left out at the report's fact limit. */
  readonly overBudget: number;
}

export interface ReportedApiRoute {
  readonly routeId: string;
  readonly status: ApiRouteStatus;
  readonly baseUrlHost: string;
  readonly keyPresent: boolean;
  readonly models: readonly OfferedModel[];
  /** The provider; `null` from connectors that predate it. */
  readonly api: ApiKind | null;
  /** What GTA last saw of the key and account; `null` before any call (Q-93). */
  readonly providerState: ProviderRouteState | null;
  /** The server's answer: `null` when it can run turns now, else why not; `blocked` is a refused key or no credit (Q-93, CP-11). */
  readonly unavailableBecause: RouteUnavailability | null;
  /** Why the route does not work, as the connector says. */
  readonly problem: string | null;
  /** Its optional facts left out at the report's shared limit: a diagnostic, never a block. */
  readonly omittedFacts: number;
}

/** How many reported facts of one route came from each source (capabilities.md CAP-1). */
export type FactSourceCounts = Readonly<Partial<Record<FactSource, number>>>;

/** The pinned GTA that calls the API-key routes' providers (ADR-0020). */
export interface ReportedGta {
  readonly version: string | null;
  readonly revision: string | null;
}

export interface ConnectorStatus {
  readonly online: boolean;
  /** ISO 8601: while online, its last message; else when its last connection ended (Q-57). */
  readonly lastSeenAt: string | null;
  /** `null` until the connector sent its first report. */
  readonly reportReceivedAt: string | null;
  /** The connector version its latest report names, the one that runs there; `null` before any report. */
  readonly runningVersion: string | null;
  readonly agents: readonly ReportedAgent[];
  readonly apiRoutes: readonly ReportedApiRoute[];
  /** The permission profiles on the PC (connector.md CN-8). */
  readonly permissionProfiles: readonly ReportedProfile[];
  /** Fact sources per route, keyed `agent:<id>` or `api_key:<id>`. */
  readonly factSources: Readonly<Record<string, FactSourceCounts>>;
  readonly gta: ReportedGta | null;
  /**
   * The connector's own tool approval page on the host's PC (connector.md TA-4), only ever a
   * `http://127.0.0.1:<port>/` address and never a token; `null` when it reports none.
   */
  readonly approvalPageUrl: string | null;
  /** How many agents the PC owner lets run there at once (connector.md CN-14); `null` before its first report. */
  readonly agentsAtOnce: AgentsAtOnce | null;
}

/** The connector's agents-at-once setting: a number of agents, or no limit. */
export type AgentsAtOnce = number | "unlimited";

export const ROUTE_UNAVAILABILITIES = enumMembers<RouteUnavailability>({ not_installed: true, not_logged_in: true, failed: true, no_key: true, blocked: true, model_unavailable: true });

export function routeKey(routeKind: "agent" | "api_key", routeId: string): string {
  return `${routeKind}:${routeId}`;
}

export function decodeConnectorStatus(status: JsonObject): ConnectorStatus {
  const report = status["report"];
  const reported = report === null ? null : readObject(report, "capability report");
  const availability = new Map(
    readArray(status["route_availability"], "route availability", (item) => {
      const route = readObject(item, "route availability");
      const because = route["unavailable_because"];
      return [routeKey(readOneOf(route, "route_kind", ["agent", "api_key"] as const), readString(route, "route_id")), because === null ? null : readOneOf(route, "unavailable_because", ROUTE_UNAVAILABILITIES)] as const;
    }),
  );
  return {
    online: readBoolean(status, "online"),
    lastSeenAt: readNullableString(status, "last_seen_at"),
    reportReceivedAt: readNullableString(status, "report_received_at"),
    runningVersion: reported === null ? null : readString(reported, "connector_version"),
    agents: reported === null ? [] : readArray(reported["agents"], "agents", (item) => decodeAgent(readObject(item, "agent"), availability)),
    apiRoutes:
      reported === null ? [] : readArray(reported["api_routes"], "API routes", (item) => decodeApiRoute(readObject(item, "API route"), availability)),
    permissionProfiles:
      reported === null
        ? []
        : readArray(reported["permission_profiles"], "permission profiles", (item) => decodeProfile(readObject(item, "profile"))),
    factSources: reported === null ? {} : countFactSources(reported["facts"]),
    gta: reported === null ? null : decodeGta(reported["gta"]),
    approvalPageUrl: reported === null ? null : decodeApprovalPage(reported["approval_page"]),
    // The report's `maximum_concurrent_turns` is the setting; `null` there is no limit.
    agentsAtOnce: reported === null ? null : reported["maximum_concurrent_turns"] === null ? "unlimited" : readInteger(reported, "maximum_concurrent_turns"),
  };
}

/** The app links only to a loopback page: anything else the server relays is ignored. */
const APPROVAL_PAGE_ADDRESS = /^http:\/\/127\.0\.0\.1:[1-9][0-9]{0,4}\/$/;

function decodeApprovalPage(page: unknown): string | null {
  const url = typeof page === "object" && page !== null ? (page as Record<string, unknown>)["url"] : undefined;
  return typeof url === "string" && APPROVAL_PAGE_ADDRESS.test(url) ? url : null;
}

function decodeProfile(profile: JsonObject): ReportedProfile {
  return {
    name: readString(profile, "name"),
    itemToolsWorkspace: profile["item_tools_workspace"] === undefined ? "none" : readOneOf(profile, "item_tools_workspace", ITEM_TOOLS_WORKSPACE),
  };
}

function countFactSources(facts: unknown): Readonly<Record<string, FactSourceCounts>> {
  const counts: Record<string, Partial<Record<FactSource, number>>> = {};
  for (const fact of readArray(facts ?? [], "facts", (item) => readObject(item, "fact"))) {
    const subject = readObject(fact["subject"], "fact subject");
    const key = routeKey(readOneOf(subject, "route_kind", ["agent", "api_key"] as const), readString(subject, "route_id"));
    const source = readOneOf(fact, "source", FACT_SOURCES);
    const route = (counts[key] ??= {});
    route[source] = (route[source] ?? 0) + 1;
  }
  return counts;
}

function decodeGta(gta: unknown): ReportedGta | null {
  if (gta === undefined || gta === null) return null;
  const described = readObject(gta, "GTA");
  return { version: readNullableString(described, "version"), revision: readNullableString(described, "revision") };
}

function decodeAgent(agent: JsonObject, availability: ReadonlyMap<string, RouteUnavailability | null>): ReportedAgent {
  const info = agent["agent_info"];
  const described = info === null ? null : readObject(info, "agent info");
  const agentId = readString(agent, "agent_id");
  return {
    agentId,
    status: readOneOf(agent, "status", AGENT_STATUSES),
    unavailableBecause: availability.get(routeKey("agent", agentId)) ?? null,
    title: described === null ? null : (readNullableString(described, "title") ?? readString(described, "name")),
    models: readModels(agent),
    modelsObservedAt: agent["models_observed_at"] === undefined ? null : readNullableString(agent, "models_observed_at"),
    defaultModelId: agent["default_model_id"] === undefined ? null : readNullableString(agent, "default_model_id"),
    reasoningEfforts: readArray(agent["reasoning_efforts"] ?? [], "reasoning efforts", (item) => {
      const effort = readObject(item, "reasoning effort");
      return { effortId: readString(effort, "effort_id"), displayName: readString(effort, "display_name") };
    }),
    catalogue: decodeCatalogue(agent["model_catalogue"]),
    problem: agent["problem"] === undefined || agent["problem"] === null ? null : readString(readObject(agent["problem"], "problem"), "detail"),
    omittedFacts: omittedFactsOf(agent),
  };
}

function decodeCatalogue(catalogue: unknown): ReportedCatalogue | null {
  if (catalogue === undefined || catalogue === null) return null;
  const read = readObject(catalogue, "model catalogue");
  return {
    state: readOneOf(read, "state", ["read", "failed"] as const),
    reported: readInteger(read, "facts_reported"),
    invalid: readInteger(read, "facts_invalid"),
    overBudget: readInteger(read, "facts_over_budget"),
  };
}

function decodeApiRoute(route: JsonObject, availability: ReadonlyMap<string, RouteUnavailability | null>): ReportedApiRoute {
  return {
    routeId: readString(route, "route_id"),
    status: readOneOf(route, "status", API_ROUTE_STATUSES),
    baseUrlHost: readString(route, "base_url_host"),
    keyPresent: readBoolean(route, "key_present"),
    models: readModels(route),
    api: route["api"] === undefined || route["api"] === null ? null : readOneOf(route, "api", API_KINDS),
    providerState: decodeProviderState(route["provider_status"]),
    unavailableBecause: availability.get(routeKey("api_key", readString(route, "route_id"))) ?? null,
    problem: route["problem"] === undefined || route["problem"] === null ? null : readString(readObject(route["problem"], "problem"), "detail"),
    omittedFacts: omittedFactsOf(route),
  };
}

/** `0` from connectors that predate it. */
function omittedFactsOf(route: JsonObject): number {
  return route["omitted_facts"] === undefined ? 0 : readInteger(route, "omitted_facts");
}

function decodeProviderState(status: unknown): ProviderRouteState | null {
  if (status === undefined || status === null) return null;
  return readOneOf(readObject(status, "provider status"), "state", PROVIDER_ROUTE_STATES);
}

function readModels(owner: JsonObject): readonly OfferedModel[] {
  return readArray(owner["models"], "models", (item) => {
    const model = readObject(item, "model");
    return { modelId: readString(model, "model_id"), displayName: readString(model, "display_name") };
  });
}
